'use client';

import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { ENGRAVING_FORM_MODAL_ID, Position, ServerActionName } from '@headless-workspace/config';
import { SideMenuModal, useToasts, useUIContext } from '@headless-workspace/core-ui';
import {
    CardBackground,
    CardSize,
    getIcon,
    GlowTextBody,
    GlowTile,
    GlowTypography,
    IconName,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import {
    getProductFormServerAction,
    ProductEngraveFormModal,
    ProductFormId,
    useEngraveContext,
    useProductVariantDetailContext,
    UserRevalidationKey,
    useSafeSWRMutation,
} from '../../../../src';

export type ProductEngraveContainerProps = {
    isVipBrand: boolean;
};

export const ProductEngraveContainer: Component<ProductEngraveContainerProps> = ({ isVipBrand }) => {
    const t = useTranslations('Modal.engrave');
    const formT = useTranslations('Common.form');
    const {
        productVariantDetails: { sku },
    } = useProductVariantDetailContext();
    const {
        menuModal: { addIfAbsentAndOpen },
    } = useUIContext();
    const { notifyError } = useToasts();
    const { engraveFormValues } = useEngraveContext();

    const { trigger: fetchEngraveFormData, isMutating } = useSafeSWRMutation(
        UserRevalidationKey.EngravingForm,
        ServerActionName.getProductForm,
        (_, { arg }: { arg: { productId: string } }) =>
            getProductFormServerAction(ProductFormId.Engrave, arg.productId),
        {
            onSuccess: (result) => {
                if (result.type === 'success') {
                    const modalInfo = {
                        modalId: ENGRAVING_FORM_MODAL_ID,
                        modal: SideMenuModal(Position.Right, {
                            default: <ProductEngraveFormModal formValueTemplate={result.data} />,
                        }),
                    };
                    addIfAbsentAndOpen(modalInfo.modalId, modalInfo.modal);
                } else {
                    notifyError(formT('error.getForm'));
                }
            },
        },
    );

    const onEngraveButtonClick = () => {
        void fetchEngraveFormData({ productId: sku });
    };

    const engraveLinesStringified = useMemo(() => {
        if (!engraveFormValues) {
            return '';
        }

        return Object.entries(engraveFormValues)
            .reduce((acc, [key, value]) => {
                if (key !== 'font' && value) {
                    return [...acc, value];
                }
                return acc;
            }, new Array<string>())
            .join(' - ');
    }, [engraveFormValues]);

    const hasEngravePendingOptions = engraveLinesStringified.length > 0;

    return (
        <GlowTile
            content={{
                hasRightIcon: true,
                Icon: getIcon(IconName.Perfum),
                isLoading: isMutating,
                className: '!h-[64px]',
            }}
            background={CardBackground.Neutral}
            TagName={'button'}
            size={CardSize.S}
            onClick={onEngraveButtonClick}
        >
            <div className={'flex flex-col items-start'}>
                <div className={'flex flex-row gap-0.5'}>
                    <GlowTextBody
                        text={hasEngravePendingOptions ? t('cta.editEngrave') : t('cta.addEngrave')}
                        fontWeight={'bold'}
                    />
                    {/* Similar to a GlowTag, but with specific colors */}
                    {/* TODO: Integrate in DS when designed */}
                    <div
                        className={clsx(
                            'rounded-0.25 flex items-center gap-0.25 p-0.25 w-fit',
                            isVipBrand ? 'bg-[#333333] text-[#E0E0E0]' : 'bg-[#004200] text-[#C0E3BC]',
                        )}
                    >
                        <GlowTypography
                            text={t('cta.freeTag')}
                            fontSize={'caption'}
                            className={'text-inherit'}
                            fontWeight={'bold'}
                            fontFamily={'brand'}
                        />
                    </div>
                </div>
                {hasEngravePendingOptions && <GlowTextBody text={engraveLinesStringified} color={'secondary'} />}
            </div>
        </GlowTile>
    );
};
